<script setup lang="ts">
import { computed } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryCounter> in <template>
import ClassicPortraitGalleryCounter from './ClassicPortraitGalleryCounter.vue';
import {
  canRetryMixedItem,
  type MixedPortraitGalleryItem,
} from './classic-mixed-portrait-gallery';
import { useMixedItemView } from './useMixedItemView';

interface Props {
  count: number | undefined;
  index: number;
  item: MixedPortraitGalleryItem;
  // Where the item stands in the list the dock shows, 1-based, and how
  // many that list holds.
  position: number;
  selected: boolean;
  total: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  activate: [event: MouseEvent];
  countDecrement: [event: MouseEvent];
  countIncrement: [event: MouseEvent];
  itemLoad: [event: Event];
  tryAgain: [];
}>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { ratio, orientation, canRetry, ready, copyKey } = useMixedItemView(
  () => props.item
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const placeholderKey = computed(() =>
  copyKey(
    props.item.isGenerated ? 'placeholderLoading' : 'placeholderGenerating'
  )
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const retryKey = computed(() => {
  if (props.item.isRetrying) {
    return copyKey('retrying');
  }
  return copyKey(
    canRetryMixedItem(props.item) ? 'buttonRetry' : 'maxRetriesReached'
  );
});
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const kindKey = computed(() =>
  props.item.kind === 'group'
    ? 'Portraits.stageKindGroup'
    : 'Portraits.stageKindIndividual'
);

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function activateFromKeyboard(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  emit('activate', new MouseEvent('click'));
}
</script>

<template>
  <section
    class="ClassicMixedPortraitGallery__focus"
    :class="{
      'ClassicMixedPortraitGallery__focus--selected': selected,
    }"
    data-testid="classic-mixed-gallery-stage"
    :data-kind="item.kind"
    :data-orientation="orientation"
    :data-uuid="item.uuid"
  >
    <div
      class="ClassicMixedPortraitGallery__focus-content"
      :style="{
        '--ClassicMixedPortraitGallery--item-ratio': String(ratio),
      }"
    >
      <button
        v-if="ready"
        class="ClassicMixedPortraitGallery__focus-action"
        type="button"
        data-testid="classic-mixed-gallery-focus-action"
        :aria-label="`${$t(copyKey('title'))} ${index + 1}`"
        :aria-pressed="selected"
        @click="emit('activate', $event)"
        @keydown="activateFromKeyboard"
      >
        <img
          class="ClassicMixedPortraitGallery__focus-image sentry-block"
          :src="item.url ?? undefined"
          :alt="`${$t(copyKey('title'))} ${index + 1}`"
          draggable="false"
          @load="emit('itemLoad', $event)"
        />
      </button>

      <div
        v-else-if="item.isError"
        class="ClassicMixedPortraitGallery__state ClassicMixedPortraitGallery__state--error"
      >
        <span>{{ $t(copyKey('error')) }}</span>
        <button
          class="ClassicMixedPortraitGallery__retry kiosk-button"
          type="button"
          :disabled="!canRetry"
          :aria-label="$t(copyKey('buttonRetry'))"
          data-testid="classic-mixed-gallery-retry"
          @click="emit('tryAgain')"
        >
          {{ $t(retryKey) }}
        </button>
      </div>

      <div v-else class="ClassicMixedPortraitGallery__state">
        <i class="ClassicMixedPortraitGallery__spinner kiosk-spinner" />
        <span>{{ $t(placeholderKey) }}</span>
      </div>

      <div class="ClassicMixedPortraitGallery__focus-caption">
        <span class="ClassicMixedPortraitGallery__focus-caption-text">
          <small>{{ $t(kindKey) }}</small>
          <strong data-testid="classic-mixed-gallery-stage-position">
            {{ $t('Portraits.stagePosition', { index: position, count: total }) }}
          </strong>
        </span>
        <span v-if="ready" class="ClassicMixedPortraitGallery__focus-actions">
          <ClassicPortraitGalleryCounter
            v-if="selected"
            inline
            :count="count"
            @decrement="emit('countDecrement', $event)"
            @increment="emit('countIncrement', $event)"
          />
          <button
            class="ClassicMixedPortraitGallery__focus-select"
            :class="{
              'ClassicMixedPortraitGallery__focus-select--selected': selected,
            }"
            type="button"
            data-testid="classic-mixed-gallery-stage-select"
            :aria-pressed="selected"
            @click="emit('activate', $event)"
          >
            {{ selected ? $t('Portraits.stageSelected') : $t('Portraits.stageSelect') }}
          </button>
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.ClassicMixedPortraitGallery__focus {
  container-type: size;
  position: relative;
  display: grid;
  place-items: center;
  min-width: 0;
  min-height: 0;
  padding: 0.75rem;
  overflow: hidden;
}

.ClassicMixedPortraitGallery__focus-content {
  position: relative;
  /* Largest box of the media ratio that fits the stage: no cropping. */
  width: min(
    100%,
    calc((100cqh - 1.5rem) * var(--ClassicMixedPortraitGallery--item-ratio))
  );
  height: auto;
  max-width: 100%;
  max-height: 100%;
  aspect-ratio: var(--ClassicMixedPortraitGallery--item-ratio);
  overflow: hidden;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.1);
  border: 1px solid rgba(var(--Kiosk--tint-color-rgb), 0.22);
  border-radius: 1rem;
  box-shadow: inset 0 0 0 1px
    rgba(var(--Kiosk--tint-inverse-color-rgb), 0.08);
  transition: box-shadow 160ms ease;
}

.ClassicMixedPortraitGallery__focus--selected
  .ClassicMixedPortraitGallery__focus-content {
  border-color: var(--Kiosk--button_background_color);
  box-shadow:
    inset 0 0 0 0.28rem var(--Kiosk--button_background_color),
    0 0.7rem 2.2rem rgba(var(--Kiosk--tint-color-rgb), 0.14);
}

.ClassicMixedPortraitGallery__focus-action {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  background: transparent;
  border: 0;
  outline: none;
}

.ClassicMixedPortraitGallery__focus-action:focus-visible {
  box-shadow: inset 0 0 0 0.28rem var(--Kiosk--button_background_color);
}

.ClassicMixedPortraitGallery__focus-image {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  object-fit: cover;
  pointer-events: none;
  -webkit-user-drag: none;
}

.ClassicMixedPortraitGallery__state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #ffffff;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  background: rgba(15, 15, 15, 0.66);
}

.ClassicMixedPortraitGallery__state::before {
  position: absolute;
  inset: 0;
  content: '';
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 255, 255, 0.16) 48%,
    transparent 66%
  );
  background-size: 240% 100%;
  animation: ClassicMixedPortraitGallery__shimmer 2.8s linear infinite;
}

.ClassicMixedPortraitGallery__state > * {
  position: relative;
  z-index: 1;
}

.ClassicMixedPortraitGallery__spinner {
  --kiosk-spinner-size: 3rem;
  --kiosk-spinner-color: #ffffff;
}

.ClassicMixedPortraitGallery__retry {
  --kiosk-button-font-size: 1.2rem;
  --kiosk-button-min-height: var(--cmg-touch);
  --kiosk-button-padding: 0.7rem 1.4rem;
}

/* The caption sits on the bottom edge of the frame: what the portrait is,
   where it stands in the list, and the select pill (with the copies
   counter once selected). It is a sibling of the image button, so the
   buttons in it are never nested in another button.

   It carries no plate of its own. A band across the bottom of the frame
   hid the part of the portrait the visitor is there to judge. The text
   stands on the image, and a tight dark edge plus a soft halo hold it
   legible against a style that renders light. */
.ClassicMixedPortraitGallery__focus-caption {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.8rem;
  align-items: center;
  justify-content: flex-end;
  padding: 0.8rem 0.9rem;
  color: var(--Kiosk--tint-inverse-color);
  pointer-events: none;
}

.ClassicMixedPortraitGallery__focus-caption > * {
  pointer-events: auto;
}

.ClassicMixedPortraitGallery__focus-caption-text {
  display: grid;
  flex: 1 1 auto;
  gap: 0.15rem;
  min-width: max-content;
  margin-right: auto;
  pointer-events: none;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.9),
    0 0 0.55rem rgba(0, 0, 0, 0.6);
}

.ClassicMixedPortraitGallery__focus-caption-text small {
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
}

.ClassicMixedPortraitGallery__focus-caption-text strong {
  font-size: 1.5rem;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

/* The counter and the pill move to the next line together when the frame
   is too narrow for the caption and both. */
.ClassicMixedPortraitGallery__focus-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 0.8rem;
  align-items: center;
  margin-left: auto;
}

.ClassicMixedPortraitGallery__focus-select {
  flex: 0 0 auto;
  min-height: var(--cmg-touch);
  padding: 0 1.6rem;
  font-family: var(--Kiosk--font_regular_url);
  font-size: 1.35rem;
  line-height: 1;
  color: var(--Kiosk--tint-inverse-color);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.55);
  border: 1px solid rgba(var(--Kiosk--tint-inverse-color-rgb), 0.65);
  border-radius: 99rem;
  outline: none;
  transition:
    background-color 140ms ease,
    color 140ms ease,
    border-color 140ms ease;
}

.ClassicMixedPortraitGallery__focus-select:focus-visible {
  box-shadow: 0 0 0 0.2rem var(--Kiosk--button_background_color);
}

.ClassicMixedPortraitGallery__focus-select--selected {
  color: var(--Kiosk--button_font_color);
  background: var(--Kiosk--button_background_color);
  border-color: var(--Kiosk--button_background_color);
}

@keyframes ClassicMixedPortraitGallery__shimmer {
  from {
    background-position: 120% 0;
  }

  to {
    background-position: -120% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ClassicMixedPortraitGallery__state::before {
    animation: none;
  }
}
</style>
